import { page } from '$app/stores';
import { PUBLIC_APP_BASE_URL } from '$env/static/public';
import { defaultLang, getAllLanguages, lang } from '$lib/lang';
import { currentLocation } from '$lib/region';
import { derived } from 'svelte/store';
import data from '$lib/region/data.json';

const appBaseUrl = PUBLIC_APP_BASE_URL || 'https://heyharper.com';

export function removeTrailingSlash(str: string) {
  return str.length > 1 ? str.replace(/\/+$/gim, '') : str;
}

export function removeMultipleSlashes(str: string) {
  return str.replace(/\/{2,}/gim, '/') || '/';
}

export function stripLanguageFromUrl(str: string) {
  const languages = getAllLanguages();
  const regex = new RegExp(`^/(${Object.keys(languages).join('|')})(/|$)`);
  return str.replace(regex, '/');
}

export function sanitizeSlug(slug: string, { stripLanguage } = { stripLanguage: false }) {
  // our stories are located in a root "pages" folder that shouldn't be considered in paths
  const path = (slug || '/').replace(/^(.[^/]+\/)?\/?(pages\/)/g, stripLanguage ? '/' : '$1/');

  return removeMultipleSlashes(removeTrailingSlash(`/${path}`));
}

export function slugify(string: string) {
  return string
    .toLowerCase()
    .trim()
    .replace(/[^\w-]/g, '-')
    .replace(/-+/g, '-');
}

type BuildPathInput = {
  path?: string;
  region?: string;
  lang?: string;
  searchQuery?: string | null;
};

const getValidSearchQuery = (query: string | null | undefined): string => {
  return query ? `?${query.replace(/^\?/, '')}` : '';
};

export const getPath = ({ region, lang, path, searchQuery }: Required<BuildPathInput>) => {
  return removeMultipleSlashes(
    removeTrailingSlash(
      `/${region}/${lang}${sanitizeSlug(path, { stripLanguage: true })}${getValidSearchQuery(
        searchQuery
      )}`
    )
  );
};

export function getFullURL(buildPathInput: Required<BuildPathInput>): string {
  return `${appBaseUrl}${getPath(buildPathInput)}`;
}

export function getCurrentPath(pageParams: Record<string, string>): string {
  // product page has no params.path, but has params.handle with hardcoded 'products' before
  return pageParams.handle ? `/products/${pageParams.handle}` : pageParams.path;
}

export const buildPath = derived(
  [currentLocation, lang, page],
  ([$currentLocation, $lang, $page]) => {
    return ((input: BuildPathInput = {}) => {
      const path = input.path || getCurrentPath($page.params) || '/';
      const region = input.region || $currentLocation;
      const regionData = data[region as keyof typeof data];
      const languageInput = input.lang || $lang;
      // remove inlinks pointing to 404 links (e.g nav links and footer links) on 404 page
      const lang = regionData.languages.includes(languageInput) ? languageInput : defaultLang;
      const searchQuery = input.searchQuery || null;

      return getPath({ path, region, lang, searchQuery });
    }) as (input: BuildPathInput) => string;
  }
);

export function getShortUrlRedirectPath(shortUrlId: string): string {
  return `/r/${shortUrlId}`;
}
